/**
 * Single source of truth for "does this location page have enough real
 * inventory to deserve indexing?". Used at runtime (robots meta) and at build
 * time (scripts/prerender.mjs decides which pages to render and sitemap).
 * Keeping both on one table means Google never gets a sitemap URL that the
 * page itself marks noindex.
 */

export type LocationPageKind = 'hub' | 'sector' | 'service' | 'condition' | 'recovery';

/** Metro-scale radius for city-level pages. NCR sprawl fits inside this. */
export const NEARBY_RADIUS_KM = 60;

/** Sub-region pages are about one locality, so they look much closer. */
export const SECTOR_RADIUS_KM = 20;

/**
 * Minimum clinics inside the radius before a page is indexable. Pages below
 * the bar still render for visitors, but carry noindex and are not
 * prerendered or listed in the sitemap. Thresholds rise with how many
 * near-identical siblings a page kind has per city.
 */
export const MIN_CLINICS: Record<LocationPageKind, number> = {
  hub: 1,
  sector: 1,
  service: 2,
  condition: 3,
  recovery: 3,
};

export const RADIUS_KM: Record<LocationPageKind, number> = {
  hub: NEARBY_RADIUS_KM,
  sector: SECTOR_RADIUS_KM,
  service: NEARBY_RADIUS_KM,
  condition: NEARBY_RADIUS_KM,
  recovery: NEARBY_RADIUS_KM,
};

/**
 * Clinic cards shown per location page. One page, no ?page=2: paginated copies
 * of a location page are near-duplicates, and sub-region pages are the
 * intended way to split a big city. Condition/recovery pages are article-led,
 * so they show fewer cards.
 */
export const LIST_LIMIT: Record<LocationPageKind, number> = {
  hub: 24,
  sector: 24,
  service: 24,
  condition: 6,
  recovery: 6,
};

export function isIndexable(kind: LocationPageKind, clinicCount: number): boolean {
  return clinicCount >= MIN_CLINICS[kind];
}

/**
 * Robots value for a location page. `null` count = results not loaded yet
 * (or failed), so stay indexable rather than flip on a transient error.
 */
export function robotsFor(kind: LocationPageKind, clinicCount: number | null | undefined): string | undefined {
  if (clinicCount == null) return undefined;
  return isIndexable(kind, clinicCount) ? undefined : 'noindex, follow';
}
